import * as React from "react";
import {
    Box,
    Button,
    Container,
    Header,
    Modal,
    SpaceBetween,
    Spinner,
    StatusIndicator,
    Table,
    Textarea
} from "@amzn/awsui-components-react";
import { EntityFactory, IPage, Page } from "@amzn/ask-legal-domain";
import { AppContext } from "../../setup/context";
import { useAPI } from "../../hooks/api-hook";
import { ErrorFlashbar } from "./ErrorFlashbar";
import { DateTimeFormatter } from "../../utils/date-time-utils";
import { IdentityLink } from "./IdentityLink";

interface AfrHistoryItem {
    action: string;
    by: string;
    date: string;
    comment: string;
}

export const AfrEnabledSection = (props: {
    page: Page.Data;
    onUpdate: (updated: Page.Data) => void;
}) => {
    const context = React.useContext(AppContext);
    const [showModal, setShowModal] = React.useState(false);
    const [comment, setComment] = React.useState("");
    const updateRunner = useAPI(context.getPageAPI().updateMetadata);
    const loadVersionsRunner = useAPI(context.getPageAPI().loadAllVersions);
    const [history, setHistory] = React.useState<AfrHistoryItem[]>([]);

    const isEnabled = props.page.afrStatus === "enabled";

    const submitLoadVersions = React.useRef(loadVersionsRunner.submitRun);
    submitLoadVersions.current = loadVersionsRunner.submitRun;

    React.useEffect(() => {
        submitLoadVersions.current(EntityFactory.toEntityRef(props.page));
    }, [props.page.version]);

    React.useEffect(() => {
        if (loadVersionsRunner.status === "Succeeded") {
            const afrEntries = loadVersionsRunner.data.output
                .filter((v: Page.Data) => v.lastModifiedCommit?.startsWith("AFR "))
                .sort((a: Page.Data, b: Page.Data) => b.version - a.version)
                .map((v: Page.Data) => ({
                    action: v.lastModifiedCommit?.includes("enabled") ? "Enabled" : "Disabled",
                    by: v.lastModifiedBy?.id ?? "",
                    date: v.lastModifiedDate ? DateTimeFormatter.formatDate(v.lastModifiedDate) : "",
                    comment: v.afrConfig?.comment || ""
                }));
            setHistory(afrEntries);
        }
    }, [loadVersionsRunner.status]);

    const handleToggle = () => {
        if (isEnabled) {
            updateRunner.submitRun(
                IPage.UpdatePageMetadataInput.create({
                    targetRef: EntityFactory.toEntityRef(props.page),
                    afrEnabled: false
                })
            );
        } else {
            setShowModal(true);
        }
    };

    const handleConfirm = () => {
        setShowModal(false);
        updateRunner.submitRun(
            IPage.UpdatePageMetadataInput.create({
                targetRef: EntityFactory.toEntityRef(props.page),
                afrEnabled: true, // Always enable — modal is only shown for the enable path
                afrComment: comment
            })
        );
        setComment("");
    };

    React.useEffect(() => {
        if (updateRunner.status === "Succeeded") {
            props.onUpdate(updateRunner.data.output);
        }
    }, [updateRunner.status]);

    return (
        <React.Fragment>
            {updateRunner.status === "Error" && <ErrorFlashbar error={updateRunner.data.err} />}
            <Container
                header={
                    <Header
                        variant="h3"
                        actions={
                            <Button
                                onClick={handleToggle}
                                loading={updateRunner.status === "Running"}
                                variant={isEnabled ? "normal" : "primary"}
                            >
                                {isEnabled ? "Disable" : "Enable"}
                            </Button>
                        }
                    >
                        Agentic First Response
                    </Header>
                }
            >
                <SpaceBetween size="l">
                    {isEnabled ? (
                        <StatusIndicator type="success">
                            AFR was enabled by {props.page.afrConfig?.updatedBy?.id} on{" "}
                            {props.page.afrConfig?.updatedDate && DateTimeFormatter.formatDate(props.page.afrConfig.updatedDate)}
                        </StatusIndicator>
                    ) : (
                        <Box variant="p" color="text-body-secondary">
                            Enable Agentic First Response to allow this page's content to be used in AI-generated ticket responses.
                        </Box>
                    )}
                    {loadVersionsRunner.status === "Running" && <Spinner />}
                    {history.length > 0 && (
                        <Table
                            columnDefinitions={[
                                { id: "action", header: "Action", cell: (item: AfrHistoryItem) => item.action },
                                { id: "by", header: "By", cell: (item: AfrHistoryItem) => <IdentityLink id={item.by} name={item.by} /> },
                                { id: "date", header: "Date", cell: (item: AfrHistoryItem) => item.date },
                                { id: "comment", header: "Comment", cell: (item: AfrHistoryItem) => item.comment || "-" },
                            ]}
                            items={history}
                            variant="embedded"
                            header={<Header variant="h3">History</Header>}
                        />
                    )}
                </SpaceBetween>
            </Container>
            <Modal
                visible={showModal}
                onDismiss={() => { setShowModal(false); setComment(""); }}
                header="Enable Agentic First Response"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => { setShowModal(false); setComment(""); }}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleConfirm}>
                                I accept the risk
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                <SpaceBetween size="m">
                    <Box variant="p">
                        <StatusIndicator type="warning">
                            Enabling AFR will allow content from this private page to be surfaced in AI-generated responses on SIM tickets.
                        </StatusIndicator>
                    </Box>
                    <Textarea
                        value={comment}
                        onChange={({ detail }) => setComment(detail.value)}
                        placeholder="Optional: Add a comment for this change"
                        rows={2}
                    />
                </SpaceBetween>
            </Modal>
        </React.Fragment>
    );
};
